import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const CmsContext = createContext({
  content: {},
  loaded: false,
  editMode: false,
  setEditMode: () => {},
  refresh: () => {},
  save: () => {},
  revert: () => {},
  registerKey: () => {},
  registry: {},
});

export function CmsProvider({ children }) {
  const [content, setContent] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [registry, setRegistry] = useState({});

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/cms/content");
      setContent(r.data || {});
    } catch (e) {
      // Public endpoint — silently ignore network errors.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async (key, html, note) => {
    const r = await api.put("/cms/content", { key, html, note: note || null });
    setContent((c) => ({ ...c, [key]: r.data.html }));
    return r.data;
  }, []);

  const revert = useCallback(async (key) => {
    await api.delete(`/cms/content/${encodeURIComponent(key)}`);
    setContent((c) => {
      const n = { ...c };
      delete n[key];
      return n;
    });
  }, []);

  const registerKey = useCallback((key, defaultHtml) => {
    setRegistry((r) => (r[key] ? r : { ...r, [key]: defaultHtml }));
  }, []);

  const value = useMemo(() => ({
    content, loaded, editMode, setEditMode, refresh, save, revert, registerKey, registry,
  }), [content, loaded, editMode, refresh, save, revert, registerKey, registry]);

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() { return useContext(CmsContext); }
